import React from "react"
import { Link } from "gatsby"

import { routes } from "../utils/routes"
import Layout from "../components/Layout"
import SEO from "@/components/SEO"
import { StaticImage } from "gatsby-plugin-image"

type PageProps = {
  data: any
}

const FAQs: React.FC<PageProps> = ({ data }) => (
  <Layout
    sectionOne={
      <>
        <div className="relative h-[67vh] md:min-h-[75vh]">
          <StaticImage
            src="../../static/images/_F7A6296.jpg"
            className="absolute inset-0 w-full h-full object-cover -z-50"
            alt="FAQs Header"
          />
          <div className="absolute inset-0 bg-[#131312]/50 -z-40"></div>

            <div className="container text-white h-[67vh] md:min-h-[75vh] flex items-center px-4">
              <div className="pl-2 md:pl-6 lg:pl-10 max-w-[1000px] translate-y-10 md:translate-y-16 lg:translate-y-20">
                <div className="pb-4">
                  <h1
                    className="text-left text-white text-[300%] sm:text-[400%] md:text-[531.25%] leading-[1] font-bold normal-case tracking-normal font-gilroy"
                  >
                    Frequently Asked Questions
                  </h1>
                  <div className="mt-5 h-[8px] sm:h-[10px] w-[200px] sm:w-[320px] bg-primary"></div>
                </div>
              </div>
            </div>
        </div>

        {/* Gold four-option bar below the fold */}
        <div className="bg-primary w-full h-[33vh] md:h-auto md:block">
          <div className="flex flex-col h-full w-full md:grid md:grid-cols-4 md:gap-0 md:items-stretch md:min-h-[84px]">
              <a href={routes.socialMobility()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Transformative Bursaries
              </a>
              <a href={routes.southWingDevelopment()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                The South Wing
              </a>
              <a href={routes.pledge()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Sponsor a Paviour
              </a>
              <a href={routes.donate()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] text-[19px] leading-[32.3px] md:px-2">
                Donate
              </a>
          </div>
        </div>
      </>
    }
  >
    <SEO title="FAQs" />
    <div className="container py-14">
      <div className="flex justify-center">
        <div className="flex flex-col gap-4 max-w-[700px]">
          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
              How can I give a Gift?
            </strong>
          </p>
          <div className="grid grid-cols-3 gap-6">
            <p className="font-gilroy">Online:</p>
            <p className="col-span-2 font-gilroy">To make a secure donation now, click <Link to={routes.donate()} className="link underline hover:no-underline">here</Link></p>

            <p className="font-gilroy">By phone:</p>
            <p className="col-span-2 font-gilroy">+44 (0) 2890 240461<br /><span className="text-sm italic mt-1 block font-gilroy">Phonelines are open weekdays, 8:15am - 4:30pm during term time. Hours vary during holiday periods.</span></p>

            <p className="font-gilroy">By post:</p>
            <p className="col-span-2 font-gilroy">You can send a one-off gift or set up a regular gift by downloading a <a
                className="link underline hover:no-underline font-gilroy-light"
                href="/docs/Donation Form MAY26.pdf"
                target="_blank"
              >donation form here</a> and returning to the address below. For one-off gifts, all cheques should be payable to 'The RBAI Foundation'.</p>
          </div>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">Why are school fees not enough to fund bursaries and capital developments?</strong>
          </p>
          <p className="font-gilroy">RBAI is a category B Grammar school. Whilst RBAI receives funding towards teaching and learning, we do not receive any capital support towards the upkeep of the school estate.</p>

          <p className="font-gilroy">The fees paid by parents fund the upkeep of the buildings and school environment. They also support the provision of an enviable range of sports, societies and other co-curricular activities.</p>

          <p className="font-gilroy">Since 2012, the Board of Governors has committed more than £10 million through a rolling capital upgrade programme, including restoring the Soane Building, an extensive refurbishment of Science facilities and the opening of a new Centre of Innovation.</p>

          <p className="font-gilroy">The Christ Church Library and ICT Centre, funded through the Millennium Campaign (1997-01) and refurbished Science laboratories and new Centre of Innovation, funded through the Excellence For All Campaign (2018-22), are a testimony to Instonian philanthropy, benefitting hundreds of boys every day.</p>

          <p className="font-gilroy">Whilst the Board of Governors currently provide means-tested bursaries for up to 10% of the pupil population, demand is growing year on year, placing increased pressure on the school budget. Support from the Instonian community is vital to ensuring that no eligible pupil is denied the opportunity of an RBAI education.</p>

          <p className="font-gilroy">Philanthropy is a critical element of the School's development plan.</p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
              Why not increase fees to fund new facilities?
            </strong>
          </p>
          <p className="font-gilroy">
            RBAI is proud to offer boys a first class education and a vast range of co-curricular activities on a par with many public schools in Great Britain, however the Board of Governors recognise that increasing fees to finance capital development would put RBAI out of reach for many families and negatively impact the School's ethos.
          </p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
              I can only give a modest amount; will my donation make any difference?
            </strong>
          </p>
          <p className="font-gilroy">
            Support from right across the Instonian community is vital in order for us to fulfil our fundraising goals.
          </p>

          <p className="font-gilroy">Making a modest regular gift such as £10 per month, is worth £450 to the School after 3 years if the donor is a UK tax payer (£10 x 36months + 25% gift aid).  </p>

          <p className="font-gilroy">A regular gift of £40 per month over 5 years will be worth £3,000 to the School, and have a remarkable impact for our pupils.   </p>

          <p className="font-gilroy">Click <Link className="link underline hover:no-underline" to={routes.donate()}>here</Link> to sign up a direct debit donation and start your support now.</p>

          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
              Is donating by credit or debit card secure?
            </strong>
          </p>
          <p className="font-gilroy">
            Yes, RBAI utilises a secure, off-site payment processor called Stripe to process one-off donations and GoCardless to process regular (recurring) direct debit donations.
          </p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
              Can RBAI claim Gift Aid on my donation?
            </strong>
          </p>
          <p className="font-gilroy">
            Yes! The Royal Belfast Academical Institution is registered with the Charity Commission for Northern Ireland (Charity Number 108024).
          </p>
          <p className="font-gilroy">So long as you have paid as much Income Tax and/or Capital Gains tax as the amount of Gift Aid claimed on all of your donations in that tax year, the school can reclaim the basic tax rate (25%) on your gift, with no extra cost to you. So your donation of £100 will actually be worth £125.</p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
            Will my donation be tax-deductible?
            </strong>
          </p>
          <p className="font-gilroy">
            UK: If you're in the UK and eligible for Gift Aid, RBAI can claim 25% back in tax from your donation. If you're a Higher Rate Tax Payer (40% and above), you can claim tax back on your charitable donations at the end of the tax year.
          </p>
          <p className="font-gilroy">
            US & Canada: Donors in the US can give to the School via the British Schools & Universities Foundation, ensuring they receive a 501c gift receipt; click <a href="https://www.bsuf.org" target="_blank" className="link underline hover:no-underline" rel="noopener noreferer">here</a>. Canadian alumni can benefit from a similar arrangement with CAF Canada; click <a href="https://www.cafcanada.ca" target="_blank" className="link underline hover:no-underline" rel="noopener noreferer">here</a>.
          </p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
            Is there a minimum or maximum donation I can make?
            </strong>
          </p>
          <p className="font-gilroy">
            The minimum online gift amount is £1. There's no maximum donation, and no limit on the impact you can make.
          </p>
          <hr className="my-5" />

          <p className="font-gilroy">
            <strong className="font-gilroy font-bold">
            How can I get in touch with the RBAI Foundation?
            </strong>
          </p>
          <p className="font-gilroy">
          Please feel free to email any questions to the RBAI Foundation Office via <a href="mailto:p.anderson@rbai.org.uk" className="link underline hover:no-underline" rel="noopener noreferer">p.anderson@rbai.org.uk</a> or call us on +44 (0)2890 240461.
          </p>
        </div>
      </div>
    </div>
  </Layout>
)

export default FAQs
